import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  type SentNotification,
  type SendNotificationInput,
  type NotificationAudience,
} from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconChat } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { dateTime } from '@/lib/adminFormat';
import { useAdminPermission } from '@/lib/useAdminPermission';

const HISTORY_LIMIT = 50;

/** Notifications — compose + send a push notification to riders/drivers, plus a
 *  log of recent sends. Push delivery is OWNED by notification-svc (the FCM
 *  fan-out + the in-app feed); admin-svc PROXIES its internal admin-send endpoint
 *  (audited + RBAC-gated server-side), so a broadcast lands as a device push AND
 *  in the app's Notifications screen.
 *
 *  COMPOSE: staff with notifications.send pick an audience (all customers, all
 *  drivers, or one specific user), type a title + body, and send. Read-only roles
 *  still see the history. The send DEFENSIVELY degrades to a clear "not available
 *  yet" message when admin-svc isn't configured to reach notification-svc.
 *
 *  HISTORY: sourced from the staff audit trail (the authoritative record of what
 *  was broadcast — notification-svc's own feed is per-end-user, not a staff log).
 *  Covers the Filament "send notification" admin surface. */
export function Notifications() {
  const canSend = useAdminPermission('notifications.send');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'notifications', 'history'],
    queryFn: ({ signal }) => adminApi.notificationHistory({ limit: HISTORY_LIMIT }, signal),
  });

  return (
    <AdminPage>
      <AdminPageHeader
        title="Notifications"
        subtitle="Compose + send a push to riders/drivers (delivered by notification-svc)."
      />

      {canSend ? (
        <ComposeCard />
      ) : (
        <Card>
          <EmptyState
            title="Sending is restricted"
            description="Your role can review the send history but not compose new notifications. Ask a superadmin or ops staffer to send a broadcast."
            icon={<IconChat className="h-6 w-6" />}
          />
        </Card>
      )}

      <div className="mt-6">
        <h2 className="mb-2 text-sm font-semibold text-slate-200">Recent sends</h2>
        <Card flush>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner label="Loading send history…" />
            </div>
          ) : isError ? (
            <EmptyState title="Couldn't load send history" description={(error as Error)?.message} />
          ) : (
            <HistoryTable rows={data?.items ?? []} />
          )}
        </Card>
      </div>
    </AdminPage>
  );
}

// ─── compose form ────────────────────────────────────────────

/** The audiences notification-svc accepts. 'all_*' are role-wide broadcasts;
 *  'user' targets one auth_users id within a scope. Mirrors the server's
 *  audience set. */
const AUDIENCE_OPTIONS: Array<{ value: NotificationAudience; label: string }> = [
  { value: 'all_customers', label: 'All customers (broadcast)' },
  { value: 'all_drivers', label: 'All drivers (broadcast)' },
  { value: 'user', label: 'A specific user (by id)' },
];

const TITLE_MAX = 120;
const BODY_MAX = 1000;

interface FormState {
  title: string;
  body: string;
  audience: NotificationAudience;
  scope: 'customer' | 'driver';
  userId: string; // bound as a string; parsed on submit
}

const EMPTY_FORM: FormState = {
  title: '',
  body: '',
  audience: 'all_customers',
  scope: 'customer',
  userId: '',
};

/** The compose card — title, body, audience selector (+ scope/user id when
 *  targeting one user), and a Send button. On success it surfaces the delivery
 *  outcome (recipients / sent / failed), resets the message, and refreshes the
 *  history list. RBAC: only ops + superadmin hold notifications.send; this card
 *  only renders for them, and admin-svc re-enforces + audits server-side. */
function ComposeCard() {
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [clientError, setClientError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<{ recipients: number; sent: number; failed: number } | null>(null);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    setClientError(null);
  };

  const mutation = useMutation({
    mutationFn: (input: SendNotificationInput) => adminApi.sendNotification(input),
    onSuccess: (res) => {
      setLastResult({ recipients: res.recipients, sent: res.sent, failed: res.failed });
      // Keep the audience selection (likely sending several in a row) but clear
      // the message so the same copy isn't accidentally re-sent.
      setForm((f) => ({ ...f, title: '', body: '' }));
      qc.invalidateQueries({ queryKey: ['admin', 'notifications', 'history'] });
    },
  });

  const onSubmit = () => {
    setClientError(null);
    setLastResult(null);
    const built = buildInput(form);
    if ('error' in built) {
      setClientError(built.error);
      return;
    }
    mutation.mutate(built.input);
  };

  const isUser = form.audience === 'user';

  return (
    <Card>
      <div className="space-y-3">
        <TextField
          label="Title"
          hint={`The bold heading on the push (max ${TITLE_MAX} chars).`}
          value={form.title}
          onChange={(v) => set('title', v)}
          placeholder="Service update"
          maxLength={TITLE_MAX}
        />

        <div>
          <label className="block text-[11px] text-muted">Message</label>
          <textarea
            value={form.body}
            onChange={(e) => set('body', e.target.value)}
            rows={3}
            maxLength={BODY_MAX}
            placeholder="Write the notification body riders/drivers will see…"
            className="mt-1 input"
          />
          <p className="mt-0.5 text-[11px] text-muted">
            {form.body.length}/{BODY_MAX}
          </p>
        </div>

        <div>
          <label className="block text-[11px] text-muted">Audience</label>
          <select
            value={form.audience}
            onChange={(e) => set('audience', e.target.value as NotificationAudience)}
            className="mt-1 input"
          >
            {AUDIENCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {isUser ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] text-muted">Recipient role</label>
              <select
                value={form.scope}
                onChange={(e) => set('scope', e.target.value as 'customer' | 'driver')}
                className="mt-1 input"
              >
                <option value="customer">Customer</option>
                <option value="driver">Driver</option>
              </select>
            </div>
            <TextField
              label="User ID"
              hint="The auth_users id of the recipient."
              value={form.userId}
              onChange={(v) => set('userId', v)}
              type="number"
              placeholder="e.g. 1842"
            />
          </div>
        ) : null}

        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={onSubmit}
            className="btn-primary"
          >
            {mutation.isPending ? 'Sending…' : 'Send notification'}
          </button>
          {clientError ? <span className="text-xs text-danger">{clientError}</span> : null}
          {mutation.isError ? <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span> : null}
          {lastResult ? (
            <span className="text-xs text-ok">
              Sent to {lastResult.recipients} recipient{lastResult.recipients === 1 ? '' : 's'}
              {lastResult.failed > 0 ? ` (${lastResult.failed} failed)` : ''}.
            </span>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

// ─── history table ───────────────────────────────────────────

/** The audience → a toned badge for the history table. */
const AUDIENCE_TONE: Record<string, BadgeTone> = {
  all_customers: 'info',
  all_drivers: 'info',
  user: 'neutral',
};

/** A human label for an audience value (+ scope/user for a single-user send). */
function audienceLabel(row: SentNotification): string {
  switch (row.audience) {
    case 'all_customers':
      return 'All customers';
    case 'all_drivers':
      return 'All drivers';
    case 'user':
      return row.user_id ? `${row.scope || 'user'} #${row.user_id}` : 'Single user';
    default:
      return row.audience || '—';
  }
}

/** The recent-sends DataTable. Each row is one staff broadcast (from the audit
 *  trail): who sent it, the title, audience, delivery counts, and the outcome. */
function HistoryTable({ rows }: { rows: SentNotification[] }) {
  const columns: Array<Column<SentNotification>> = [
    {
      key: 'created_at',
      header: 'Sent',
      cell: (r) => <span className="metric text-xs text-slate-300">{dateTime(r.created_at)}</span>,
    },
    {
      key: 'title',
      header: 'Title',
      cell: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-slate-100">{r.title || '—'}</div>
          {r.preview ? <div className="truncate text-[11px] text-muted">{r.preview}</div> : null}
        </div>
      ),
    },
    {
      key: 'audience',
      header: 'Audience',
      cell: (r) => <Badge tone={AUDIENCE_TONE[r.audience] ?? 'neutral'}>{audienceLabel(r)}</Badge>,
    },
    {
      key: 'recipients',
      header: 'Recipients',
      align: 'right',
      cell: (r) => <span className="metric text-xs text-slate-300">{r.recipients}</span>,
    },
    {
      key: 'delivered',
      header: 'Delivered',
      align: 'right',
      cell: (r) => (
        <span className="metric text-xs">
          <span className="text-ok">{r.sent}</span>
          {r.failed > 0 ? <span className="text-danger"> / {r.failed} failed</span> : null}
        </span>
      ),
    },
    {
      key: 'by',
      header: 'By',
      cell: (r) => <span className="text-xs text-slate-300">{r.actor_email || '—'}</span>,
    },
    {
      key: 'result',
      header: 'Result',
      cell: (r) => (
        <Badge tone={r.result === 'ok' ? 'ok' : 'danger'} dot>
          {r.result === 'ok' ? 'sent' : 'failed'}
        </Badge>
      ),
    },
  ];

  return (
    <DataTable<SentNotification>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={
        <EmptyState
          title="No notifications sent yet"
          description="Broadcasts you send will appear here."
          icon={<IconChat className="h-6 w-6" />}
        />
      }
    />
  );
}

// ─── form helpers ────────────────────────────────────────────

/** A labelled text/number input row (matches the Promos drawer's TextField). */
function TextField({
  label,
  hint,
  value,
  onChange,
  type = 'text',
  placeholder,
  maxLength,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  type?: 'text' | 'number';
  placeholder?: string;
  maxLength?: number;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="mt-1 input"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

/** Validate + convert the form into a SendNotificationInput, or return an error
 *  string. Mirrors admin-svc's light pre-check so the staffer sees a precise
 *  message instantly; the server re-validates (this is a UX nicety, not the
 *  boundary). */
function buildInput(f: FormState): { input: SendNotificationInput } | { error: string } {
  const title = f.title.trim();
  if (!title) return { error: 'Title is required.' };
  if (title.length > TITLE_MAX) return { error: `Title must be ${TITLE_MAX} characters or fewer.` };

  const body = f.body.trim();
  if (!body) return { error: 'Message is required.' };
  if (body.length > BODY_MAX) return { error: `Message must be ${BODY_MAX} characters or fewer.` };

  if (f.audience === 'user') {
    const id = Number(f.userId.trim());
    if (!f.userId.trim() || Number.isNaN(id) || id <= 0) {
      return { error: 'Enter a valid user id (a positive number) for a single-user notification.' };
    }
    return { input: { title, body, audience: 'user', scope: f.scope, user_id: id } };
  }

  return { input: { title, body, audience: f.audience } };
}
